import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/money";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn, Empty, StatusPill, dialogCls } from "@/components/admin/ui";
import { ContractEditor } from "@/components/admin/ContractEditor";

export const ContractsPanel = ({ clients, templates, openNewFor, clearOpenNew }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/admin/contracts").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (openNewFor) {
      setEditing({ client_id: openNewFor, title: "", body: "", status: "draft" });
      clearOpenNew();
    }
  }, [openNewFor, clearOpenNew]);

  const open = async (c) => {
    try {
      const { data } = await api.get(`/admin/contracts/${c.id}`);
      setEditing(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const onSaved = (c) => setItems((xs) => (xs.some((x) => x.id === c.id) ? xs.map((x) => (x.id === c.id ? { ...x, ...c } : x)) : [c, ...xs]));

  const act = async (c, action) => {
    if (!window.confirm(action === "void" ? "Void this contract? The client link will stop working." : "Delete this contract permanently?")) return;
    try {
      if (action === "void") await api.post(`/admin/contracts/${c.id}/void`);
      else await api.delete(`/admin/contracts/${c.id}`);
      setItems((xs) => (action === "void" ? xs.map((x) => (x.id === c.id ? { ...x, status: "void" } : x)) : xs.filter((x) => x.id !== c.id)));
      setEditing(null);
      toast.success(action === "void" ? "Contract voided" : "Contract deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="contracts-panel">
      <div className="mb-4 flex justify-end">
        <Btn variant="primary" onClick={() => setEditing({ client_id: "", title: "", body: "", status: "draft" })} disabled={!clients.length} data-testid="contract-new-btn"><Plus className="h-4 w-4" /> New contract</Btn>
      </div>
      {!items.length ? <Empty testId="contracts-empty">No contracts yet. {clients.length ? "Create one from a template." : "Add a client first."}</Empty> : (
        <div className="divide-y divide-[#2E2A24] overflow-hidden rounded-2xl border border-[#2E2A24]">
          {items.map((c) => (
            <div key={c.id} className="flex items-center gap-2 bg-[#121215] pr-3 hover:bg-[#1A1A20]">
              <button onClick={() => open(c)} className="grid min-w-0 flex-1 grid-cols-1 gap-1 px-5 py-4 text-left sm:grid-cols-12 sm:items-center" data-testid={`contract-row-${c.id}`}>
                <span className="font-medium text-[#F5EFE6] sm:col-span-5">{c.title}</span>
                <span className="text-sm text-zinc-300 sm:col-span-3">{c.client_name}</span>
                <span className="text-xs text-zinc-400 sm:col-span-2">{fmtDate(c.created_at)}</span>
                <span className="sm:col-span-2 sm:text-right"><StatusPill status={c.status} /></span>
              </button>
              {c.status !== "signed" ? (
                <Btn variant="danger" onClick={() => act(c, "delete")} aria-label="Delete contract" data-testid={`contract-row-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Btn>
              ) : <span className="w-12" title="Signed contracts can't be deleted" />}
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className={dialogCls}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing?.id ? editing.title : "New contract"}</DialogTitle></DialogHeader>
          {editing && <ContractEditor key={editing.id || "new"} initial={editing} clients={clients} templates={templates} onSaved={onSaved} />}
          {editing?.id && (
            <div className="flex gap-4 border-t border-[#2E2A24] pt-4">
              {editing.status !== "void" && <Btn variant="danger" onClick={() => act(editing, "void")} data-testid="contract-void-btn">Void</Btn>}
              {editing.status !== "signed" && <Btn variant="danger" onClick={() => act(editing, "delete")} data-testid="contract-delete-btn">Delete</Btn>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
